import React, { useEffect, useState } from "react";
import { Search, X } from "lucide-react";
import { getReports } from "@/lib/serverComm";
import { ReportForUI } from "@/types/report";
import { formatDate } from "@/common/dateUtils";

interface SearchReportsProps {
  onChange: (reports: ReportForUI[]) => void;
}

const SearchReports: React.FC<SearchReportsProps> = ({ onChange }) => {
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [prevQuery, setPrevQuery] = useState("prev");
  const [reports, setReports] = useState<ReportForUI[]>([]);
  const [selectedReports, setSelectedReports] = useState<ReportForUI[]>([]);

  useEffect(() => {
    if (isSearching) return;
    if (searchQuery === prevQuery) return;
    const doSearch = async (query: string) => {
      setIsSearching(true);
      const result = await getReports(1000, query);
      setReports(result);
      setIsSearching(false);
    };

    doSearch(searchQuery);
    setPrevQuery(searchQuery);
  }, [searchQuery, prevQuery, isSearching]);

  const toggleReport = (report: ReportForUI) => {
    let newSelectedReports: ReportForUI[] = [];
    const selectedReportIds = selectedReports.map((report) => report.id);
    if (selectedReportIds.includes(report.id)) {
      newSelectedReports = selectedReports.filter((r) => r.id !== report.id);
    } else {
      newSelectedReports = [...selectedReports, report];
    }

    setSelectedReports(newSelectedReports);
    onChange(newSelectedReports);
  };

  const reportsContent =
    reports.length === 0 ? (
      <div className="text-center text-deck-primary py-8">
        No items match your search. Try adjusting your search and try again.
      </div>
    ) : (
      reports.map((report) => (
        <div
          key={report.id}
          className="p-3 rounded-md border border-deck-500 shadow flex gap-x-3 items-center"
          onClick={() => toggleReport(report)}
        >
          <input
            type="checkbox"
            checked={selectedReports.map((r) => r.id).includes(report.id)}
            onChange={() => toggleReport(report)}
            className="text-deck-primary"
          />

          <span className="font-semibold text-deck-primary">
            {report.title}
          </span>
          <span className="text-deck-primary ml-auto text-sm">
            {formatDate(new Date(report.updated_at))}
          </span>
        </div>
      ))
    );
  return (
    <div className="block">
      {/* Search Reports */}
      <div className="block pb-4">
        <span className="block text-deck-primary font-semibold mb-4">
          Select or search your reports
        </span>
        <div className="group relative transition w-[420px] px-3 py-2 bg-white border border-deck-borders rounded-lg focus-within:border-deck-primary">
          <Search className="absolute text-deck-800" size={24} />
          <X
            className={`${
              isSearchFocused ? "block" : "hidden"
            } absolute right-2 text-deck-primary cursor-pointer`}
            size={24}
          />
          <input
            type="text"
            value={searchQuery}
            placeholder="Search for a report"
            className="w-full px-8 py-0 border-0 focus:outline-0 focus:ring-0"
            onChange={(e) => setSearchQuery(e.target.value)}
            onFocus={() => setIsSearchFocused(true)}
            onBlur={() => setIsSearchFocused(false)}
          />
        </div>
      </div>

      {/* List slides */}
      <div className="block pb-10 flex flex-col gap-y-3 h-80 pr-4 overflow-y-auto">
        {reportsContent}
      </div>
      {/* List slides */}
    </div>
  );
};
export default SearchReports;
